import { map, values, mapObjIndexed, flatten } from "ramda";
import numeral from "numeral";
import "numeral/locales/de";

import i18n from "@/i18n";
import reportHeader from "./header";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import fundInfo from "./fundInfo";
import stars from "./stars";
import { getDisclaimer } from "@/utils/getDisclaimer";
import { images } from "./images";
import markdownToPdfmake from "@fng/common/markdownToPdfmake";

const green = "#95ad4b";
const blue = "#426eac";

const h1 = {
  bold: true,
  fontSize: 16,
  pageBreak: "before",
  margin: [0, 0, 0, 17],
  headlineLevel: 1,
};

const h2 = {
  bold: true,
  fontSize: 13,
  margin: [0, 0, 0, 12],
  headlineLevel: 2,
};

const requirementTable = {
  fontSize: 8.5,
  margin: [0, 0, 0, 17],
  layout: {
    hLineColor: "#888888",
    hLineWidth: function(i, node) {
      if (i === 0) return 0;
      if (i === node.table.body.length) return 0; // why not -1?

      return 0.5;
    },
    vLineWidth: function() {
      return 0;
    },
    paddingLeft: (i) => {
      if (i > 0) return 2;
      return 0;
    },
    paddingRight: (i, node) => {
      if (i < node.table.widths.length - 1) return 2;
      return 0;
    },
  },
};

const summary = (fund, audit) => {
  const result = [];

  if (fund.precheck) {
    result.push({
      text: i18n.t("reportSummary.precheck", fund.language),
      fontSize: 13,
      bold: true,
      margin: [0, 0, 0, 6],
    });

    result.push({
      stack: markdownToPdfmake(fund.report.fundResult.minimalRequirementsText),
      alignment: "justify",
      fontSize: 10,
      margin: [0, 0, 0, 17],
    });
  } else {
    result.push({
      text: i18n.t("reportSummary.minimal", fund.language),
      fontSize: 13,
      bold: true,
      margin: [0, 0, 0, 6],
    });

    result.push(
      fund.report.minimalRequirementsResult === "PASS"
        ? {
            text: i18n.t("fund.report.minimalRequirementsPassed", fund.language),
            alignment: "justify",
            fontSize: 10,
            margin: [0, 0, 0, 17],
          }
        : {
            stack: markdownToPdfmake(fund.report.fundResult.minimalRequirementsText),
            alignment: "justify",
            fontSize: 10,
            margin: [0, 0, 0, 17],
          }
    );
  }

  const audited = fund.report.minimalRequirementsResult === "PASS";

  if (!(audited && fund.report.stepModelCategories)) return result;

  result.push({
    text: i18n.t("reportSummary.step", fund.language),
    fontSize: 13,
    bold: true,
    margin: [0, 0, 0, 6],
  });

  result.push({
    stack: markdownToPdfmake(fund.report.fundResult.stepModelRequirementsText),
    alignment: "justify",
    fontSize: 10,
    margin: [0, 0, 0, 17],
  });

  const header = [
    {
      text: i18n.t("reportSummary.finalResult", fund.language),
      fontSize: 13,
      bold: true,
      margin: [0, 0, 0, 6],
      color: blue,
    },
    {
      // text: `${fund.report.stars} ${i18n.tc("report.stars", fund.report.stars, fund.language)}`,
      stack: [stars(fund.report.stars, blue, 15)],
      fontSize: 13,
      bold: true,
      margin: [0, -1, 0, 7],
    },
  ];

  if (audit) {
    header.push("");
    header.push(numeral(fund.report.perc).format("0%"));
  }

  const body = [
    header,
    ...map((category) => {
      const row = [
        i18n.t(`stepModelRequirementCategory.${category.category}.name`, fund.language),
        {
          // text: `${category.stars} ${i18n.tc("report.stars", category.stars, fund.language)}`,
          stack: [stars(category.stars, green, 15)],
          margin: [0, -2, 0, 0],
        },
      ];

      if (audit) {
        row.push(`${category.points} / ${category.maxPoints}`);
        row.push(numeral(category.perc).format("0%"));
      }

      return row;
    }, fund.report.categoriesWithStars),
  ];

  const widths = audit ? ["*", 50, 50, 50] : ["*", 200];

  result.push({
    layout: "noBorders",
    table: {
      widths,
      body,
    },
    margin: [0, 0, 0, 20],
    fontSize: 11,
  });

  return result;
};

const minimalRequirements = (fund, requirements, audit) => {
  const categories = groupByCategory(requirements);

  const header = {
    ...h1,
    text: i18n.t(fund.precheck ? "fund.report.precheck.title" : "fund.report.minimalRequirements.title", fund.language),
  };

  const widths = audit ? ["*", "*", 30] : ["*", "*"];

  const cats = values(
    mapObjIndexed((category, index) => {
      return [
        {
          ...h2,
          color: green,
          text: i18n.t(`minimalRequirementCategory.${index}.name`, fund.language),
        },
        {
          ...requirementTable,
          table: {
            widths,
            body: map((requirement) => {
              const row = [
                [
                  [
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.name`, fund.language),
                      bold: true,
                    },
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.description`, fund.language),
                    },
                  ],
                ],
                markdownToPdfmake(requirement.result.description),
              ];

              if (audit) row.push(requirement.result.result);

              return row;
            }, category),
          },
        },
      ];
    }, categories)
  );

  return [header, ...cats];
};

const stepModelRequirements = (fund, categories, audit) => {
  const audited = fund.report.minimalRequirementsResult === "PASS";

  const header = {
    ...h1,
    text: i18n.t("fund.report.stepModelRequirements.title", fund.language),
  };

  const cats = map((category) => {
    let result = [];

    if (category.weight) {
      result.push({
        ...h2,
        text: i18n.t(`stepModelRequirementCategory.${category.category}.name`, fund.language),
        color: green,
      });

      result.push({
        columns: [
          {
            width: "auto",
            text: i18n.t(`fund.report.2022.category.finalResult`, fund.language),
            fontSize: 8.5,
          },
          {
            width: "auto",
            stack: [stars(category.stars, green, 12)],
            margin: [4, -2, 0, 0],
          },
        ],
        margin: [0, -6, 0, 11],
      });
    } else {
      result.push({
        ...h2,
        text: i18n.t(`stepModelRequirementCategory.${category.category}.name`, fund.language),
      });
    }

    const requirements = map((requirement) => {
      const row = [
        [
          {
            text: i18n.t(`stepModelRequirement.${requirement.requirementId}.name`, fund.language),
            bold: true,
          },
          {
            text: i18n.t(`stepModelRequirement.${requirement.requirementId}.description`, fund.language),
            bold: false,
          },
        ],
        audited
          ? markdownToPdfmake(requirement.result.description)
          : i18n.t(`fund.report.requirementNotAudited`, fund.language),
      ];

      if (audit) row.push(requirement.result.points || "");

      return row;
    }, category.requirements);

    const widths = audit ? ["*", "*", 30] : ["*", "*"];

    if (!requirements.length) return result;

    const table = {
      ...requirementTable,
      table: {
        widths,
        body: requirements,
      },
    };

    return [result, table];
  }, categories);

  return [header, cats];
};

const flattenStepModelCategories = (categories) =>
  flatten(
    map(
      (category) => (category.children.length ? [category, flattenStepModelCategories(category.children)] : category),
      categories
    )
  );

/**
 * Date the certification was granted.
 *
 * The provisional report is handed out once the audit is finished, the final one once
 * the fund is closed. Neither date may lie before the audit decision, so finalAuditAt
 * is the lower bound in both cases.
 *
 * @TODO drop the updatedAt fallback once certifiedAt is backfilled for the older funds
 */
const certificationDate = (fund, type) => {
  const granted = type === "provisional" ? fund.finalAuditAt : fund.certifiedAt;

  const dates = [granted || fund.updatedAt, fund.finalAuditAt].filter(Boolean).map((date) => new Date(date));

  return new Date(Math.max(...dates));
};

/**
 * Regulatory information on the front page (AIR address, certification date, scope, validity).
 * The text is maintained in the translations and only rendered once it exists there.
 * The provisional report has its own text ("Vorläufiges Zertifizierungsdatum") if there is one.
 */
const certification = (fund, type) => {
  const baseKey = "fund.report.2025.certificationText";
  const provisionalKey = `${baseKey}Provisional`;
  const key = type === "provisional" && i18n.te(provisionalKey, fund.language) ? provisionalKey : baseKey;

  if (fund.precheck || !i18n.te(key, fund.language)) return null;

  const text = i18n.t(key, fund.language, {
    date: certificationDate(fund, type).toLocaleDateString(fund.language),
    year: fund.auditPeriod.year + 1,
  });

  return {
    // blank lines in the translation separate the blocks
    stack: markdownToPdfmake(text, { keepEmptyLines: true }),
    alignment: "justify",
    fontSize: 10,
    lineHeight: 1.25,
    margin: [0, 25, 0, 0],
  };
};

/**
 * Electronic signature of the certification body on the front page.
 * Label and signee are maintained in the translations, the signature itself is a static image.
 */
const signature = (fund) => {
  const labelKey = "fund.report.2025.signatureLabel";
  const signeeKey = "fund.report.2025.signatureSignee";

  if (fund.precheck) return null;

  const stack = [];

  if (i18n.te(labelKey, fund.language)) {
    stack.push({ text: i18n.t(labelKey, fund.language), bold: true, margin: [0, 0, 0, 5] });
  }

  stack.push({ image: "signature", width: 165 });
  stack.push({ text: "______________________________", margin: [0, 5, 0, 5] });

  if (i18n.te(signeeKey, fund.language)) {
    stack.push(...markdownToPdfmake(i18n.t(signeeKey, fund.language)));
  }

  return {
    stack,
    fontSize: 10,
    lineHeight: 1.25,
    margin: [0, 15, 0, 0],
  };
};

const generateReportPdfData = (fund, type, audit = false) => {
  numeral.locale(fund.language);

  const title = {
    text: fund.reportTitle
      ? fund.reportTitle
      : i18n.t(fund.precheck ? `fund.report.2025.titlePrecheck` : `fund.report.2025.title`, fund.language, {
          nextYear: fund.auditPeriod.year + 1,
        }),
    font: "RobotoThin",
    fontSize: 36,
    lineHeight: 1.25,
    margin: [0, 80, 0, 0],
  };

  const name = {
    text: fund.name,
    fontSize: 14,
    lineHeight: 1.25,
    bold: true,
    margin: [0, 40, 0, 0],
  };

  const company = {
    text: fund.user.company.address.name,
    fontSize: 14,
    lineHeight: 1.25,
  };

  const frontPage = [
    title,
    name,
    company,
    certification(fund, type),
    signature(fund),
    { text: null, pageBreak: "after" },
  ];

  let result = minimalRequirements(fund, fund.report.minimalRequirements, audit);

  if (fund.report.stepModelCategories) {
    result = result.concat(
      stepModelRequirements(fund, flattenStepModelCategories(fund.report.stepModelCategories), audit)
    );
  }

  const disclaimer = {
    text: getDisclaimer(fund),
    fontSize: 6,
    color: "#888888",
    margin: [37, 0, 37, 0],
  };

  return {
    header: () => {
      if (type !== "provisional") return;

      return {
        text: i18n.t("fund.report.provisionalInfo", fund.language),
        bold: true,
        fontSize: 20,
        alignment: "center",
      };
    },

    footer: (currentPage, pageCount) => {
      if (currentPage === 1) return reportHeader(fund);

      const page = currentPage - 1;
      const count = pageCount - 1;

      return [
        {
          text: `${page.toString()} | ${count.toString()}`,
          alignment: "center",
          margin: [0, 17, 0, 8],
          fontSize: 8,
        },
        disclaimer,
      ];
    },
    content: [frontPage, fundInfo(fund), summary(fund, audit), result],
    defaultStyle: {
      font: "Roboto",
      fontSize: 8.5,
    },
    pageMargins: [37, 65, 37, 110],
    pageBreakBefore: function(currentNode, followingNodesOnPage) {
      return currentNode.headlineLevel === 2 && followingNodesOnPage.length < 9; // includes the 2 footer items, the number was picked by testing
    },
    images,
  };
};

export default generateReportPdfData;
